import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Users, Plane, Plus, Trash2, Pencil, Download, Loader2, BellRing, Eye } from "lucide-react";
import { ModuleHeaderIcon } from "@/components/ModuleHeader";

const ESTATUS_DOC = {
  vigente: "bg-emerald-100 text-emerald-700",
  por_vencer: "bg-amber-100 text-amber-700",
  vencido: "bg-red-100 text-red-700",
  sin_fecha: "bg-slate-100 text-slate-600",
};
const ESTATUS_DOC_LABEL = { vigente: "Vigente", por_vencer: "Por vencer", vencido: "Vencido", sin_fecha: "Sin fecha" };
const TRIP_STATUS = {
  programado: { label: "Programado", cls: "bg-blue-100 text-blue-700" },
  en_curso: { label: "En curso", cls: "bg-amber-100 text-amber-700" },
  concluido: { label: "Concluido", cls: "bg-emerald-100 text-emerald-700" },
};
const EMPTY_LEG = { aerolinea: "", numero_vuelo: "", origen: "", destino: "", fecha: "", hora_salida: "", hora_llegada: "" };
const openPicker = (e) => { try { e.currentTarget.showPicker?.(); } catch { /* navegadores sin soporte */ } };

const DocBadge = ({ profile, tipo, label }) => {
  const doc = (profile.documentos || []).find((d) => d.tipo === tipo);
  if (!doc) return <Badge variant="outline" className="text-slate-400 border-slate-200">{label}: —</Badge>;
  return <Badge className={`${ESTATUS_DOC[doc.estatus]} border-0`}>{label}: {doc.fecha_vencimiento || ESTATUS_DOC_LABEL[doc.estatus]}</Badge>;
};

export default function ViajeroInterno() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [profiles, setProfiles] = useState([]);
  const [trips, setTrips] = useState([]);
  const [airlines, setAirlines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [edits, setEdits] = useState({});
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileDelete, setProfileDelete] = useState(null);
  const [tripDialog, setTripDialog] = useState(null); // null | {trip fields}
  const [savingTrip, setSavingTrip] = useState(false);
  const [runningAlerts, setRunningAlerts] = useState(false);
  const [tripWarn, setTripWarn] = useState(null); // null | 'pasado' | 'hoy'
  const [tripDelete, setTripDelete] = useState(null);
  const [tripView, setTripView] = useState(null);
  const [tripF, setTripF] = useState({ q: "", status: "todos", desde: "", hasta: "", regPor: "todos", regDesde: "", regHasta: "" });

  const load = useCallback(async () => {
    try {
      const [pRes, tRes, aRes] = await Promise.all([
        axios.get(`${API}/viajeros/perfiles`, { withCredentials: true }),
        axios.get(`${API}/viajeros/viajes`, { withCredentials: true }),
        axios.get(`${API}/airlines`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setProfiles(pRes.data || []);
      setTrips(tRes.data || []);
      setAirlines(aRes.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al cargar datos");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setEditMode(false); setEdits({}); }, [detail?.id]);

  const setEdit = (k, v) => setEdits((e) => ({ ...e, [k]: v }));

  const saveProfile = async () => {
    if (!detail?.id) return;
    if (!Object.keys(edits).length) { toast.info("Sin cambios"); return; }
    setSavingProfile(true);
    try {
      const r = await axios.patch(`${API}/viajeros/perfiles/${detail.id}`, { updates: edits }, { withCredentials: true });
      setDetail(r.data);
      setEdits({});
      setEditMode(false);
      toast.success("Perfil actualizado");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally {
      setSavingProfile(false);
    }
  };

  const deleteProfile = async (p) => {
    try {
      const r = await axios.delete(`${API}/viajeros/perfiles/${p.id}`, { withCredentials: true });
      setProfiles((ps) => ps.filter((x) => x.id !== p.id));
      setDetail(null);
      toast.success(`Perfil eliminado (${r.data?.viajes_eliminados || 0} viaje(s) removidos)`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al eliminar");
    }
  };

  const openNewTrip = () => setTripDialog({ profile_id: "", destino: "", motivo: "", fecha_inicio: "", fecha_fin: "", hotel: "", pnr: "", notas: "", vuelos: [{ ...EMPTY_LEG }] });
  const openEditTrip = (t) => setTripDialog({ ...t, _orig_inicio: t.fecha_inicio, _orig_fin: t.fecha_fin, vuelos: (t.vuelos?.length ? t.vuelos : [{ ...EMPTY_LEG }]).map((v) => ({ ...v })) });

  const setTrip = (k, v) => setTripDialog((d) => ({ ...d, [k]: v }));
  const setLeg = (i, k, v) => setTripDialog((d) => {
    const vuelos = d.vuelos.map((l, idx) => (idx === i ? { ...l, [k]: v } : l));
    return { ...d, vuelos };
  });

  const saveTrip = async () => {
    if (!tripDialog.profile_id) return toast.error("Selecciona el viajero");
    if (!tripDialog.destino?.trim()) return toast.error("Indica el destino");
    if (!tripDialog.fecha_inicio) return toast.error("Indica la fecha de inicio");
    // Aviso si el viaje es en fecha pasada o inicia hoy (solo si es nuevo o cambiaron las fechas)
    const fechasCambiaron = !tripDialog.id ||
      tripDialog.fecha_inicio !== tripDialog._orig_inicio || (tripDialog.fecha_fin || "") !== (tripDialog._orig_fin || "");
    if (fechasCambiaron) {
      const hoy = new Date().toLocaleDateString("sv-SE", { timeZone: "America/Mexico_City" });
      const fin = tripDialog.fecha_fin || tripDialog.fecha_inicio;
      if (fin < hoy) return setTripWarn("pasado");
      if (tripDialog.fecha_inicio <= hoy) return setTripWarn("hoy");
    }
    await doSaveTrip();
  };

  const doSaveTrip = async () => {
    setSavingTrip(true);
    try {
      const { _orig_inicio, _orig_fin, ...rest } = tripDialog;
      const payload = { ...rest, vuelos: tripDialog.vuelos.filter((v) => v.aerolinea || v.numero_vuelo || v.origen || v.destino) };
      if (tripDialog.id) {
        await axios.put(`${API}/viajeros/viajes/${tripDialog.id}`, payload, { withCredentials: true });
        toast.success("Viaje actualizado");
      } else {
        await axios.post(`${API}/viajeros/viajes`, payload, { withCredentials: true });
        toast.success("Viaje registrado");
      }
      setTripDialog(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar el viaje");
    } finally {
      setSavingTrip(false);
    }
  };

  const toggleCheck = async (trip, field, value) => {
    try {
      const res = await axios.patch(`${API}/viajeros/viajes/${trip.id}/checks`, { [field]: value }, { withCredentials: true });
      setTrips((ts) => ts.map((t) => (t.id === trip.id ? res.data : t)));
      toast.success(value ? "Check activado" : "Check desactivado");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al actualizar");
    }
  };

  const deleteTrip = async (trip) => {
    try {
      await axios.delete(`${API}/viajeros/viajes/${trip.id}`, { withCredentials: true });
      setTrips((ts) => ts.filter((t) => t.id !== trip.id));
      toast.success("Viaje eliminado");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al eliminar");
    }
  };

  const downloadDoc = async (doc) => {
    try {
      const res = await axios.get(`${API}/viajeros/archivo/${doc.storage_path}`, { withCredentials: true, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      toast.error("No se pudo descargar el archivo");
    }
  };

  const runAlerts = async () => {
    setRunningAlerts(true);
    try {
      await axios.post(`${API}/viajeros/alertas/run-now`, {}, { withCredentials: true });
      toast.success("Ciclo de alertas ejecutado");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al ejecutar alertas");
    } finally {
      setRunningAlerts(false);
    }
  };

  if (loading) return <div className="p-8 flex items-center gap-2 text-slate-500"><Loader2 className="w-5 h-5 animate-spin" /> Cargando…</div>;

  const tripsFiltrados = trips.filter((t) => {
    if (tripF.q.trim()) {
      const q = tripF.q.trim().toLowerCase();
      if (![t.traveler_name, t.destino, t.motivo, t.pnr].some((v) => (v || "").toLowerCase().includes(q))) return false;
    }
    if (tripF.status !== "todos" && t.status !== tripF.status) return false;
    if (tripF.desde && (t.fecha_inicio || "") < tripF.desde) return false;
    if (tripF.hasta && (t.fecha_inicio || "") > tripF.hasta) return false;
    if (tripF.regPor !== "todos" && (t.created_by_name || "") !== tripF.regPor) return false;
    const reg = (t.created_at || "").slice(0, 10);
    if (tripF.regDesde && reg < tripF.regDesde) return false;
    if (tripF.regHasta && reg > tripF.regHasta) return false;
    return true;
  });

  const registradores = [...new Set(trips.map((t) => t.created_by_name).filter(Boolean))].sort();
  const tripFActivo = tripF.q || tripF.status !== "todos" || tripF.desde || tripF.hasta || tripF.regPor !== "todos" || tripF.regDesde || tripF.regHasta;

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="viajero-interno-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <ModuleHeaderIcon icon={Users} />
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Perfil de Viajero · Viajero Interno</h1>
            <p className="text-sm text-slate-500 mt-1">Perfiles de viajeros internos (staff) y registro de sus viajes.</p>
          </div>
        </div>
        <div className="flex gap-2">
          {user?.role === "admin" && (
            <Button variant="outline" onClick={runAlerts} disabled={runningAlerts} data-testid="run-alerts-btn">
              {runningAlerts ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <BellRing className="w-4 h-4 mr-2" />} Ejecutar alertas
            </Button>
          )}
          <Button onClick={openNewTrip} className="bg-cyan-700 hover:bg-cyan-800" data-testid="new-trip-btn"><Plus className="w-4 h-4 mr-2" /> Registrar viaje</Button>
        </div>
      </div>

      <Tabs defaultValue="perfiles">
        <TabsList>
          <TabsTrigger value="perfiles" data-testid="tab-perfiles">Perfiles ({profiles.length})</TabsTrigger>
          <TabsTrigger value="viajes" data-testid="tab-viajes">Viajes ({trips.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="perfiles">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Viajero</TableHead>
                    <TableHead>Departamento</TableHead>
                    <TableHead>Correo</TableHead>
                    <TableHead>Documentos (vencimiento)</TableHead>
                    <TableHead className="w-24 text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {profiles.length === 0 && (
                    <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-8">Aún no hay perfiles. Cada colaborador debe llenar su perfil en &quot;Mi Perfil Viajero&quot;.</TableCell></TableRow>
                  )}
                  {profiles.map((p) => (
                    <TableRow key={p.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setDetail(p)} data-testid={`profile-row-${p.id}`}>
                      <TableCell className="font-medium">{p.nombre_completo || <span className="text-slate-400">Sin nombre</span>}</TableCell>
                      <TableCell>{p.departamento || "—"}</TableCell>
                      <TableCell className="text-slate-500">{p.email}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1.5">
                          <DocBadge profile={p} tipo="pasaporte" label="Pasaporte" />
                          <DocBadge profile={p} tipo="id" label="ID" />
                          {p.visa_tiene && <DocBadge profile={p} tipo="visa" label="Visa" />}
                        </div>
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="sm" onClick={() => setDetail(p)} data-testid={`view-profile-${p.id}`} title="Ver / editar"><Eye className="w-4 h-4" /></Button>
                          {isAdmin && (
                            <Button variant="ghost" size="sm" onClick={() => setProfileDelete(p)} className="text-red-500 hover:text-red-600 hover:bg-red-50" title="Eliminar perfil (solo admin)" data-testid={`delete-profile-${p.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="viajes">
          <Card>
            <CardContent className="p-4 border-b border-slate-100">
              <div className="grid grid-cols-12 gap-3 items-end">
                <div className="col-span-12 sm:col-span-4">
                  <Label className="text-xs text-slate-500">Buscar (viajero, destino, motivo, PNR)</Label>
                  <Input value={tripF.q} onChange={(e) => setTripF({ ...tripF, q: e.target.value })} placeholder="Escribe para filtrar…" data-testid="trip-filtro-q" />
                </div>
                <div className="col-span-6 sm:col-span-2">
                  <Label className="text-xs text-slate-500">Estatus</Label>
                  <Select value={tripF.status} onValueChange={(v) => setTripF({ ...tripF, status: v })}>
                    <SelectTrigger data-testid="trip-filtro-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todos">Todos</SelectItem>
                      <SelectItem value="programado">Programado</SelectItem>
                      <SelectItem value="en_curso">En curso</SelectItem>
                      <SelectItem value="concluido">Concluido</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-6 sm:col-span-3">
                  <Label className="text-xs text-slate-500">Registrado por</Label>
                  <Select value={tripF.regPor} onValueChange={(v) => setTripF({ ...tripF, regPor: v })}>
                    <SelectTrigger data-testid="trip-filtro-regpor"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todos">Todos</SelectItem>
                      {registradores.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-12 sm:col-span-3 flex justify-end">
                  {tripFActivo && (
                    <Button variant="ghost" size="sm" className="text-slate-400" onClick={() => setTripF({ q: "", status: "todos", desde: "", hasta: "", regPor: "todos", regDesde: "", regHasta: "" })} data-testid="trip-filtro-limpiar">Limpiar filtros</Button>
                  )}
                </div>
                <div className="col-span-12 sm:col-span-6 grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs text-slate-500">Fecha de vuelo · desde</Label>
                    <Input type="date" className="cursor-pointer" onClick={openPicker} value={tripF.desde} onChange={(e) => setTripF({ ...tripF, desde: e.target.value })} data-testid="trip-filtro-desde" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Fecha de vuelo · hasta</Label>
                    <Input type="date" className="cursor-pointer" onClick={openPicker} value={tripF.hasta} onChange={(e) => setTripF({ ...tripF, hasta: e.target.value })} data-testid="trip-filtro-hasta" />
                  </div>
                </div>
                <div className="col-span-12 sm:col-span-6 grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs text-slate-500">Fecha de registro · desde</Label>
                    <Input type="date" className="cursor-pointer" onClick={openPicker} value={tripF.regDesde} onChange={(e) => setTripF({ ...tripF, regDesde: e.target.value })} data-testid="trip-filtro-reg-desde" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Fecha de registro · hasta</Label>
                    <Input type="date" className="cursor-pointer" onClick={openPicker} value={tripF.regHasta} onChange={(e) => setTripF({ ...tripF, regHasta: e.target.value })} data-testid="trip-filtro-reg-hasta" />
                  </div>
                </div>
              </div>
            </CardContent>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Fecha de registro</TableHead>
                    <TableHead>Registrado por</TableHead>
                    <TableHead>Viajero</TableHead>
                    <TableHead>Destino</TableHead>
                    <TableHead>Motivo</TableHead>
                    <TableHead>PNR</TableHead>
                    <TableHead>Fechas</TableHead>
                    <TableHead>Estatus</TableHead>
                    <TableHead>Seguro emitido</TableHead>
                    <TableHead>Docs enviados</TableHead>
                    <TableHead className="w-24"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tripsFiltrados.length === 0 && (
                    <TableRow><TableCell colSpan={11} className="text-center text-slate-400 py-8">{trips.length === 0 ? "No hay viajes registrados." : "Ningún viaje coincide con los filtros."}</TableCell></TableRow>
                  )}
                  {tripsFiltrados.map((t) => {
                    const st = TRIP_STATUS[t.status] || TRIP_STATUS.programado;
                    return (
                      <TableRow key={t.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setTripView(t)} data-testid={`trip-row-${t.id}`}>
                        <TableCell className="text-slate-500 text-sm whitespace-nowrap">{(t.created_at || "").slice(0, 10) || "—"}</TableCell>
                        <TableCell className="text-slate-500 text-sm">{t.created_by_name || "—"}</TableCell>
                        <TableCell className="font-medium">{t.traveler_name}</TableCell>
                        <TableCell>{t.destino}</TableCell>
                        <TableCell className="text-slate-500 text-sm">{t.motivo || "—"}</TableCell>
                        <TableCell className="text-slate-600 text-sm font-mono">{t.pnr || "—"}</TableCell>
                        <TableCell className="text-slate-500 text-sm">{t.fecha_inicio} → {t.fecha_fin || "—"}</TableCell>
                        <TableCell><Badge className={`${st.cls} border-0`}>{st.label}</Badge></TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Switch checked={!!t.seguro_emitido} disabled={!!t.registrado_pasado}
                            onCheckedChange={(v) => toggleCheck(t, "seguro_emitido", v)} data-testid={`switch-seguro-${t.id}`} />
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Switch checked={!!t.documentos_enviados} disabled={!!t.registrado_pasado}
                            onCheckedChange={(v) => toggleCheck(t, "documentos_enviados", v)} data-testid={`switch-docs-${t.id}`} />
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" onClick={() => openEditTrip(t)} data-testid={`edit-trip-${t.id}`}><Pencil className="w-4 h-4" /></Button>
                            <Button variant="ghost" size="sm" onClick={() => setTripDelete(t)} data-testid={`delete-trip-${t.id}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Detalle de perfil */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="profile-detail-dialog">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 flex-wrap">
                  {detail.nombre_completo || detail.email}
                  {isAdmin && !editMode && (
                    <Button size="sm" variant="outline" onClick={() => setEditMode(true)} className="ml-auto border-cyan-300 text-cyan-700 hover:bg-cyan-50" data-testid="admin-edit-profile-btn">
                      <Pencil className="w-4 h-4 mr-1.5" /> Editar (admin)
                    </Button>
                  )}
                </DialogTitle>
              </DialogHeader>

              {!editMode ? (
                <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                  {[
                    ["Correo", detail.email], ["Teléfono", detail.telefono], ["Departamento", detail.departamento],
                    ["Nacimiento", detail.fecha_nacimiento], ["Nacionalidad", detail.nacionalidad],
                    ["No. Pasaporte", detail.pasaporte_numero], ["Vence pasaporte", detail.pasaporte_vencimiento],
                    ["Visa", detail.visa_tiene ? `${detail.visa_tipo || "Sí"} (vence ${detail.visa_vencimiento || "—"})` : "No"],
                    ["Viajero confiable", detail.viajero_confiable_numero],
                    ["Aerolínea pref.", detail.aerolinea_preferida], ["Lealtad", detail.programas_lealtad],
                    ["Asiento", detail.asiento_preferencia], ["Cadena hotelera", detail.cadena_hotelera],
                    ["Alimentación", detail.preferencias_alimentarias],
                    ["Emergencia", detail.emergencia_nombre ? `${detail.emergencia_nombre} (${detail.emergencia_parentesco || "—"}) ${detail.emergencia_telefono || ""}` : null],
                    ["Cond. médicas", detail.condiciones_medicas], ["Alergias", detail.alergias], ["Tipo de sangre", detail.tipo_sangre],
                    ["Tarjetas corp.", (detail.tarjetas_corporativas || []).length
                      ? detail.tarjetas_corporativas.map((t) => `${t.tipo || "?"} **** ${t.ult4 || "----"}`).join(", ")
                      : (detail.tarjeta_corporativa_ult4 ? `**** ${detail.tarjeta_corporativa_ult4}` : null)], ["Talla", detail.talla],
                    ["Consentimiento", detail.consentimiento_aceptado ? "Aceptado" : "Pendiente"],
                  ].map(([k, v]) => (
                    <div key={k}><span className="text-slate-400">{k}:</span> <span className="text-slate-700">{v || "—"}</span></div>
                  ))}
                </div>
              ) : (
                <div className="rounded-lg border border-cyan-100 bg-cyan-50/40 p-3">
                  <p className="text-xs text-cyan-800 mb-3 font-medium">Modo edición administrativa · Cada cambio se registra en el log</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                    {[
                      ["nombre_completo", "Nombre completo"],
                      ["departamento", "Departamento"],
                      ["telefono", "Teléfono"],
                      ["fecha_nacimiento", "Fecha nacimiento", "date"],
                      ["nacionalidad", "Nacionalidad"],
                      ["pasaporte_numero", "No. pasaporte"],
                      ["pasaporte_vencimiento", "Vence pasaporte", "date"],
                      ["id_vencimiento", "Vence ID", "date"],
                      ["visa_tipo", "Tipo de visa"],
                      ["visa_vencimiento", "Vence visa", "date"],
                      ["viajero_confiable_numero", "Viajero confiable"],
                      ["aerolinea_preferida", "Aerolínea preferida"],
                      ["programas_lealtad", "Programas de lealtad"],
                      ["asiento_preferencia", "Preferencia de asiento"],
                      ["cadena_hotelera", "Cadena hotelera"],
                      ["preferencias_alimentarias", "Alimentación"],
                      ["emergencia_nombre", "Contacto emergencia"],
                      ["emergencia_parentesco", "Parentesco"],
                      ["emergencia_telefono", "Tel. emergencia"],
                      ["tipo_sangre", "Tipo de sangre"],
                      ["alergias", "Alergias"],
                      ["condiciones_medicas", "Condiciones médicas"],
                      ["talla", "Talla"],
                    ].map(([k, label, type]) => (
                      <div key={k} className="space-y-1">
                        <Label className="text-xs text-slate-500">{label}</Label>
                        <Input
                          type={type || "text"}
                          className={type === "date" ? "cursor-pointer h-9" : "h-9"}
                          onClick={type === "date" ? openPicker : undefined}
                          value={(edits[k] !== undefined ? edits[k] : (detail[k] ?? "")) || ""}
                          onChange={(e) => setEdit(k, e.target.value)}
                          data-testid={`admin-edit-${k}`}
                        />
                      </div>
                    ))}
                    <div className="sm:col-span-2 flex items-center gap-2">
                      <Switch checked={edits.visa_tiene !== undefined ? edits.visa_tiene : !!detail.visa_tiene} onCheckedChange={(v) => setEdit("visa_tiene", v)} data-testid="admin-edit-visa_tiene" />
                      <Label className="text-xs text-slate-500">Tiene visa</Label>
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-2">
                <p className="text-sm font-semibold text-slate-700 mb-2">Documentos</p>
                <div className="space-y-2">
                  {(detail.documentos || []).length === 0 && <p className="text-sm text-slate-400">Sin documentos.</p>}
                  {(detail.documentos || []).map((d) => (
                    <div key={d.id} className="flex items-center justify-between border border-slate-200 rounded-lg px-3 py-2 text-sm">
                      <div className="flex items-center gap-2 min-w-0">
                        <Badge className={`${ESTATUS_DOC[d.estatus]} border-0`}>{ESTATUS_DOC_LABEL[d.estatus]}</Badge>
                        <span className="font-medium uppercase text-xs">{d.tipo}</span>
                        <span className="text-slate-500 truncate">{d.filename}</span>
                        <span className="text-slate-400 text-xs">vence {d.fecha_vencimiento || "—"}</span>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => downloadDoc(d)} data-testid={`download-doc-${d.id}`}><Download className="w-4 h-4" /></Button>
                    </div>
                  ))}
                </div>
              </div>

              {isAdmin && (detail.actualizado_por || []).length > 0 && (
                <div className="mt-3 border-t border-slate-100 pt-3">
                  <p className="text-sm font-semibold text-slate-700 mb-2">Log de cambios ({(detail.actualizado_por || []).length})</p>
                  <div className="space-y-1 max-h-48 overflow-y-auto pr-1" data-testid="admin-audit-log">
                    {(detail.actualizado_por || []).slice().reverse().map((l, i) => (
                      <div key={i} className="text-xs text-slate-600 border-l-2 border-cyan-300 pl-2 py-0.5">
                        <span className="text-slate-400">{(l.at || "").slice(0, 16).replace("T", " ")}</span> ·{" "}
                        <b className="text-slate-700">{l.editor}</b>{" "}
                        <Badge variant="outline" className="text-[10px] py-0 px-1.5 ml-1">
                          {l.via === "admin" ? "admin" : "usuario"}
                        </Badge>{" "}
                        {l.accion === "eliminar" ? <span className="text-red-600">eliminó el perfil</span> : <>editó: <span className="text-slate-500">{(l.campos || []).join(", ") || "—"}</span></>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <DialogFooter className="flex-wrap gap-2 sm:justify-between">
                <div className="flex items-center gap-2">
                  {isAdmin && !editMode && (
                    <Button variant="outline" size="sm" className="border-red-300 text-red-600 hover:bg-red-50" onClick={() => setProfileDelete(detail)} data-testid="admin-delete-profile-btn">
                      <Trash2 className="w-4 h-4 mr-1.5" /> Eliminar perfil
                    </Button>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {editMode ? (
                    <>
                      <Button variant="outline" onClick={() => { setEditMode(false); setEdits({}); }}>Cancelar</Button>
                      <Button onClick={saveProfile} disabled={savingProfile || !Object.keys(edits).length} className="bg-cyan-700 hover:bg-cyan-800" data-testid="admin-save-profile-btn">
                        {savingProfile ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                        Guardar cambios {Object.keys(edits).length ? `(${Object.keys(edits).length})` : ""}
                      </Button>
                    </>
                  ) : (
                    <Button variant="outline" onClick={() => setDetail(null)}>Cerrar</Button>
                  )}
                </div>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Diálogo de viaje */}
      <Dialog open={!!tripDialog} onOpenChange={(o) => !o && setTripDialog(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="trip-dialog">
          {tripDialog && (
            <>
              <DialogHeader><DialogTitle className="flex items-center gap-2"><Plane className="w-5 h-5 text-cyan-700" /> {tripDialog.id ? "Editar viaje" : "Registrar viaje"}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Viajero *</Label>
                  <Select value={tripDialog.profile_id || undefined} onValueChange={(v) => setTrip("profile_id", v)} disabled={!!tripDialog.id}>
                    <SelectTrigger data-testid="select-trip-viajero"><SelectValue placeholder="Selecciona…" /></SelectTrigger>
                    <SelectContent>{profiles.map((p) => <SelectItem key={p.id} value={p.id}>{p.nombre_completo || p.email}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Destino *</Label>
                  <Input value={tripDialog.destino || ""} onChange={(e) => setTrip("destino", e.target.value)} data-testid="input-trip-destino" />
                </div>
                <div className="space-y-1.5">
                  <Label>Fecha inicio *</Label>
                  <Input type="date" className="cursor-pointer" onClick={openPicker} value={tripDialog.fecha_inicio || ""} onChange={(e) => setTrip("fecha_inicio", e.target.value)} data-testid="input-trip-inicio" />
                </div>
                <div className="space-y-1.5">
                  <Label>Fecha fin</Label>
                  <Input type="date" className="cursor-pointer" onClick={openPicker} value={tripDialog.fecha_fin || ""} onChange={(e) => setTrip("fecha_fin", e.target.value)} data-testid="input-trip-fin" />
                </div>
                <div className="space-y-1.5">
                  <Label>Motivo</Label>
                  <Input value={tripDialog.motivo || ""} onChange={(e) => setTrip("motivo", e.target.value)} data-testid="input-trip-motivo" />
                </div>
                <div className="space-y-1.5">
                  <Label>Hotel</Label>
                  <Input value={tripDialog.hotel || ""} onChange={(e) => setTrip("hotel", e.target.value)} data-testid="input-trip-hotel" />
                </div>
                <div className="space-y-1.5">
                  <Label>PNR (código de reserva)</Label>
                  <Input value={tripDialog.pnr || ""} onChange={(e) => setTrip("pnr", e.target.value.toUpperCase())} placeholder="Ej. ABC123" data-testid="input-trip-pnr" />
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="font-semibold">Vuelos</Label>
                  <Button type="button" variant="outline" size="sm" onClick={() => setTripDialog((d) => ({ ...d, vuelos: [...d.vuelos, { ...EMPTY_LEG }] }))} data-testid="add-leg-btn"><Plus className="w-4 h-4 mr-1" /> Tramo</Button>
                </div>
                {tripDialog.vuelos.map((v, i) => {
                  const terrestre = v.aerolinea === "Traslado Terrestre";
                  return (
                    <div key={i} className="border border-slate-100 rounded-lg p-2 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Tramo {i + 1}</span>
                        <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-red-500 hover:text-red-600 hover:bg-red-50" title="Eliminar este tramo"
                          onClick={() => setTripDialog((d) => ({ ...d, vuelos: d.vuelos.filter((_, idx) => idx !== i) }))} data-testid={`remove-leg-${i}`}>
                          <Trash2 className="w-4 h-4 mr-1" /> Quitar tramo
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
                        <div className="space-y-1 md:col-span-2">
                          <Label className="text-xs">{terrestre ? "Tipo de traslado" : "Aerolínea"}</Label>
                          <Select value={v.aerolinea || undefined} onValueChange={(val) => setLeg(i, "aerolinea", val)}>
                            <SelectTrigger className="h-9" data-testid={`leg-aerolinea-${i}`}><SelectValue placeholder="—" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Traslado Terrestre">🚐 Traslado Terrestre</SelectItem>
                              {airlines.map((a) => <SelectItem key={a.id} value={a.name}>{a.name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1"><Label className="text-xs">{terrestre ? "Folio / # de viaje" : "Vuelo"}</Label><Input className="h-9" value={v.numero_vuelo} onChange={(e) => setLeg(i, "numero_vuelo", e.target.value)} data-testid={`leg-vuelo-${i}`} /></div>
                        <div className="space-y-1"><Label className="text-xs">Origen</Label><Input className="h-9" value={v.origen} onChange={(e) => setLeg(i, "origen", e.target.value)} /></div>
                        <div className="space-y-1"><Label className="text-xs">Destino</Label><Input className="h-9" value={v.destino} onChange={(e) => setLeg(i, "destino", e.target.value)} /></div>
                        <div className="space-y-1"><Label className="text-xs">Fecha</Label><Input className="h-9 cursor-pointer" type="date" onClick={openPicker} value={v.fecha} onChange={(e) => setLeg(i, "fecha", e.target.value)} /></div>
                        <div className="space-y-1"><Label className="text-xs">Salida</Label><Input className="h-9" type="time" value={v.hora_salida} onChange={(e) => setLeg(i, "hora_salida", e.target.value)} /></div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="space-y-1.5">
                <Label>Notas</Label>
                <Textarea rows={2} value={tripDialog.notas || ""} onChange={(e) => setTrip("notas", e.target.value)} data-testid="input-trip-notas" />
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setTripDialog(null)}>Cancelar</Button>
                <Button onClick={saveTrip} disabled={savingTrip} className="bg-cyan-700 hover:bg-cyan-800" data-testid="save-trip-btn">
                  {savingTrip ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Guardando…</>) : "Guardar viaje"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Detalle de viaje (clic en fila) */}
      <Dialog open={!!tripView} onOpenChange={(o) => !o && setTripView(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="trip-view-dialog">
          {tripView && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Plane className="w-5 h-5 text-cyan-700" /> {tripView.traveler_name} → {tripView.destino}
                  <Badge className={`${(TRIP_STATUS[tripView.status] || TRIP_STATUS.programado).cls} border-0 ml-2`}>
                    {(TRIP_STATUS[tripView.status] || TRIP_STATUS.programado).label}
                  </Badge>
                </DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                {[
                  ["Registrado", `${(tripView.created_at || "").slice(0, 10)} ${tripView.created_by_name ? `· ${tripView.created_by_name}` : ""}`],
                  ["Fechas del viaje", `${tripView.fecha_inicio} → ${tripView.fecha_fin || "—"}`],
                  ["Motivo", tripView.motivo], ["Hotel", tripView.hotel],
                  ["PNR", tripView.pnr],
                  ["Seguro emitido", tripView.seguro_emitido ? `Sí ${tripView.seguro_emitido_by ? `(${tripView.seguro_emitido_by})` : ""}` : "No"],
                  ["Docs enviados", tripView.documentos_enviados ? `Sí ${tripView.documentos_enviados_by ? `(${tripView.documentos_enviados_by})` : ""}` : "No"],
                  ["Alertas", tripView.alertas_desactivadas ? "Desactivadas" : "Activas"],
                ].map(([k, v]) => (
                  <div key={k}><span className="text-slate-400">{k}:</span> <span className="text-slate-700">{v || "—"}</span></div>
                ))}
              </div>
              {(tripView.vuelos || []).length > 0 && (
                <div className="mt-2">
                  <p className="text-sm font-semibold text-slate-700 mb-2">Vuelos / Traslados</p>
                  <div className="space-y-2">
                    {tripView.vuelos.map((v, i) => (
                      <div key={i} className="flex items-center gap-3 border border-slate-200 rounded-lg px-3 py-2 text-sm flex-wrap">
                        <Badge variant="outline" className="text-slate-500">Tramo {i + 1}</Badge>
                        <span className="font-medium">{v.aerolinea || "—"}</span>
                        <span className="text-slate-500">{v.aerolinea === "Traslado Terrestre" ? "Folio / # de viaje" : "Vuelo"}: {v.numero_vuelo || "—"}</span>
                        <span className="text-slate-500">{v.origen || "?"} → {v.destino || "?"}</span>
                        <span className="text-slate-400 text-xs">{v.fecha || "—"} {v.hora_salida || ""}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {tripView.notas && (
                <div className="mt-2 text-sm">
                  <p className="font-semibold text-slate-700 mb-1">Notas</p>
                  <p className="text-slate-600 whitespace-pre-line bg-slate-50 rounded-lg p-3">{tripView.notas}</p>
                </div>
              )}
              <DialogFooter>
                <Button variant="outline" onClick={() => setTripView(null)} data-testid="trip-view-cerrar">Cerrar</Button>
                <Button className="bg-cyan-700 hover:bg-cyan-800" onClick={() => { const t = tripView; setTripView(null); openEditTrip(t); }} data-testid="trip-view-editar">
                  <Pencil className="w-4 h-4 mr-2" /> Editar viaje
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Aviso: viaje en fecha pasada o que inicia hoy */}
      <AlertDialog open={!!tripWarn} onOpenChange={(o) => !o && setTripWarn(null)}>
        <AlertDialogContent data-testid="trip-fecha-warn-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{tripWarn === "pasado" ? "Viaje en fecha pasada" : "El viaje inicia hoy"}</AlertDialogTitle>
            <AlertDialogDescription>
              {tripWarn === "pasado"
                ? "Estás registrando un viaje en una fecha pasada. Se guardará con estatus CONCLUIDO, no se recibirán alertas de ningún tipo y los checks de Seguro emitido / Docs enviados quedarán inactivos (no se podrán mover)."
                : "Estás registrando un viaje que inicia hoy. Se guardará con estatus EN CURSO y no se detonará ninguna alerta. Los checks de Seguro emitido / Docs enviados sí podrán activarse."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="trip-warn-cancelar">Cancelar</AlertDialogCancel>
            <AlertDialogAction className="bg-cyan-700 hover:bg-cyan-800"
              onClick={() => { setTripWarn(null); doSaveTrip(); }} data-testid="trip-warn-continuar">
              Continuar y guardar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirmación de eliminación de perfil (solo admin) */}
      <AlertDialog open={!!profileDelete} onOpenChange={(o) => !o && setProfileDelete(null)}>
        <AlertDialogContent data-testid="profile-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar el perfil de {profileDelete?.nombre_completo || profileDelete?.email}?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción eliminará el perfil junto con todos sus viajes registrados. La eliminación quedará registrada en el log de auditoría del sistema.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="profile-delete-cancelar">Cancelar</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700"
              onClick={() => { const p = profileDelete; setProfileDelete(null); deleteProfile(p); }} data-testid="profile-delete-confirmar">
              Eliminar perfil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirmación de eliminación de viaje */}
      <AlertDialog open={!!tripDelete} onOpenChange={(o) => !o && setTripDelete(null)}>
        <AlertDialogContent data-testid="trip-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar el viaje de {tripDelete?.traveler_name} a {tripDelete?.destino}?</AlertDialogTitle>
            <AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="trip-delete-cancelar">Cancelar</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700"
              onClick={() => { const t = tripDelete; setTripDelete(null); deleteTrip(t); }} data-testid="trip-delete-confirmar">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
